import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EquationDefinition, EquationGroup, ImpactPreviewOutput, PolicyError, PolicyInput, PublishPolicyScope } from 'used-vehicle-contracts';
import { buildValuationInputs, compilePolicy, INPUT_CATALOG, referenceSampleBundle, runForOffer, type UnitName } from 'used-vehicle-engine';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { FormField } from '../../../components/ui/FormField';
import { Select } from '../../../components/ui/Select';
import { Spinner } from '../../../components/ui/Spinner';
import { Textarea } from '../../../components/ui/Textarea';
import { isConflictError, serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { EquationRow } from './EquationRow';
import { ImpactPreviewCard } from './ImpactPreviewCard';
import {
  EQUATION_GROUP_ORDER,
  equationsSignature,
  errorsByEquation,
  formatEquationResult,
  groupEquations,
  nextCustomId,
  policyWideErrors,
  referencesTo,
  toEngineEquations,
} from './equations-display';
import { PolicyVersionsTable } from './PolicyVersionsTable';
import { SamplePreviewCard, type SampleFacts, type SampleValuesOption } from './SamplePreviewCard';

/** Long enough that a burst of keystrokes is one save, short enough that leaving the page is safe. */
const SAVE_DEBOUNCE_MS = 700;

const SAMPLE_DEFAULTS: SampleFacts = { valuationYear: 2026, modelYear: 2025, odometerKm: 42_000, ownerCount: 1 };

/**
 * Settings → Equations (plan §7).
 *
 * The page an administrator edits the pricing model in. Three things make it work:
 *
 *  - **The preview is local.** Every keystroke recompiles and re-runs the whole policy against a sample
 *    vehicle through the `used-vehicle-engine` package — the same code the service runs — so a result
 *    or an error appears under each expression with no request in between (plan §2.5).
 *  - **The draft is a real version.** The first edit opens a DRAFT row; every later edit saves into it,
 *    debounced and carrying `expectedRevision`, so a second administrator editing the same draft in
 *    another tab produces a translated 409 rather than a silent overwrite (plan §2.3).
 *  - **Publishing needs the server's word.** Local compilation is a convenience; the Publish button only
 *    unlocks after `policies.validateDraft` has approved *exactly* the equations now on screen.
 */
export const EquationsPage = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [equations, setEquations] = useState<EquationDefinition[]>([]);
  const [reason, setReason] = useState('');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [sample, setSample] = useState<SampleFacts>(SAMPLE_DEFAULTS);
  const [valuesFrom, setValuesFrom] = useState('');
  const [validation, setValidation] = useState<{ signature: string; ok: boolean; errors: PolicyError[] } | null>(null);
  const [impact, setImpact] = useState<{ signature: string; result: ImpactPreviewOutput } | null>(null);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [publishScope, setPublishScope] = useState<PublishPolicyScope>('NEW');
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const publishedQuery = useQuery(orpc.policies.getPublished.queryOptions());
  const versionsQuery = useQuery(orpc.policies.listVersions.queryOptions());
  const catalogQuery = useQuery(orpc.policies.inputCatalog.queryOptions());

  // The phase-2 hook: the sample can be priced with a real template's published values instead of the
  // built-in preset, which is what makes "same formula, two caps" visible on this page (PP AC20).
  const templatesQuery = useQuery(orpc.templates.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' } }));

  const valuesOptions = useMemo<SampleValuesOption[]>(
    () =>
      (templatesQuery.data?.items ?? [])
        .filter((template) => template.publishedVersionId !== null && template.publishedVersion !== null)
        .map((template) => ({
          templateId: template.id,
          versionId: template.publishedVersionId!,
          label: template.name,
          version: template.publishedVersion!,
        })),
    [templatesQuery.data],
  );

  const selectedValues = valuesOptions.find((option) => option.templateId === valuesFrom) ?? null;

  const templateVersionQuery = useQuery(
    orpc.templates.getVersion.queryOptions({ input: { versionId: selectedValues?.versionId ?? '' }, enabled: selectedValues !== null }),
  );

  const templateValues = selectedValues !== null && templateVersionQuery.data ? templateVersionQuery.data : null;

  const draftSummary = versionsQuery.data?.find((version) => version.status === 'DRAFT') ?? null;

  const draftQuery = useQuery(
    orpc.policies.getVersion.queryOptions({ input: { versionId: draftSummary?.id ?? '' }, enabled: Boolean(draftSummary) }),
  );

  const source = draftQuery.data ?? publishedQuery.data ?? null;

  // Load the draft (or, when there is none, the published policy) into the editor exactly once per
  // version — a later save must not be clobbered by the query that produced the original copy.
  useEffect(() => {
    if (!source) {
      return;
    }

    const key = `${source.id}:${reloadToken}`;

    if (loadedKey === key) {
      return;
    }

    setLoadedKey(key);
    setEquations(source.equations.map((equation) => ({ ...equation })));
    setReason(source.status === 'DRAFT' ? (source.reason ?? '') : '');
    setDraftId(source.status === 'DRAFT' ? source.id : null);
    setRevision(source.status === 'DRAFT' ? source.revision : null);
    setValidation(null);
  }, [source, loadedKey, reloadToken]);

  const createDraft = useMutation(orpc.policies.createDraft.mutationOptions());
  const updateDraft = useMutation(orpc.policies.updateDraft.mutationOptions());
  const validateDraft = useMutation(orpc.policies.validateDraft.mutationOptions());
  const publishPolicy = useMutation(orpc.policies.publish.mutationOptions());

  const draftIdRef = useRef<string | null>(null);
  const revisionRef = useRef<number | null>(null);
  const pendingRef = useRef<{ equations: EquationDefinition[]; reason: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  draftIdRef.current = draftId;
  revisionRef.current = revision;

  const invalidateVersions = useCallback(async () => queryClient.invalidateQueries({ queryKey: orpc.policies.listVersions.key() }), [queryClient]);

  /** Writes whatever is pending. Opens the draft first when this is the very first edit. */
  const flush = useCallback(async (): Promise<void> => {
    const pending = pendingRef.current;

    if (!pending) {
      return;
    }

    pendingRef.current = null;
    setIsSaving(true);
    setActionError(null);

    try {
      let id = draftIdRef.current;
      let expectedRevision = revisionRef.current;

      if (id === null || expectedRevision === null) {
        const created = await createDraft.mutateAsync({});

        id = created.id;
        expectedRevision = created.revision;
        draftIdRef.current = id;
        revisionRef.current = expectedRevision;
        setDraftId(id);
        setRevision(expectedRevision);
        setLoadedKey(`${id}:${reloadToken}`);
        await invalidateVersions();
      }

      const saved = await updateDraft.mutateAsync({
        versionId: id,
        expectedRevision,
        equations: pending.equations,
        reason: pending.reason,
      });

      revisionRef.current = saved.revision;
      setRevision(saved.revision);
      setSavedAt(Date.now());
    } catch (error: unknown) {
      if (isConflictError(error)) {
        setActionError(t('errors.staleRevision'));
        setReloadToken((token) => token + 1);
        await Promise.all([invalidateVersions(), draftQuery.refetch()]);

        return;
      }

      setActionError(serverMessage(error) ?? t(submitErrorKey(error)));
    } finally {
      setIsSaving(false);
    }
  }, [createDraft, draftQuery, invalidateVersions, reloadToken, t, updateDraft]);

  const scheduleSave = useCallback(
    (nextEquations: EquationDefinition[], nextReason: string) => {
      pendingRef.current = { equations: nextEquations, reason: nextReason };

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const applyEquations = useCallback(
    (next: EquationDefinition[]) => {
      setEquations(next);
      setValidation(null);
      scheduleSave(next, reason);
    },
    [reason, scheduleSave],
  );

  const changeEquation = useCallback(
    (id: string, patch: Partial<EquationDefinition>) => {
      applyEquations(equations.map((equation) => (equation.id === id ? { ...equation, ...patch } : equation)));
    },
    [applyEquations, equations],
  );

  const addCustomEquation = useCallback(
    (group: EquationGroup) => {
      const custom: EquationDefinition = {
        id: nextCustomId(equations),
        kind: 'CUSTOM',
        scope: 'ALWAYS',
        label: t('equations.customDefaultLabel'),
        unit: 'JOD',
        group,
        expression: '0',
      };

      applyEquations([...equations, custom]);
    },
    [applyEquations, equations, t],
  );

  const removeEquation = useCallback((id: string) => applyEquations(equations.filter((equation) => equation.id !== id)), [applyEquations, equations]);

  const changeReason = useCallback(
    (next: string) => {
      setReason(next);
      scheduleSave(equations, next);
    },
    [equations, scheduleSave],
  );

  // ── the local preview ───────────────────────────────────────────────────────────────────────────
  const preview = useMemo(() => {
    const compiled = compilePolicy(toEngineEquations(equations));
    const bundle = referenceSampleBundle(sample);
    const built = buildValuationInputs(
      templateValues
        ? {
            ...bundle,
            template: {
              params: templateValues.params,
              damageGroup1: templateValues.damageGroup1,
              damageGroup2: templateValues.damageGroup2,
              overridableKeys: templateValues.overridableKeys,
            },
          }
        : bundle,
    );
    const run = runForOffer(compiled.policy, built.inputs, { tradeIn: built.tradeIn, costResolutions: built.costResolutions });
    const labels = new Map(equations.map((equation) => [equation.id, equation.label]));

    const errors: PolicyError[] = compiled.errors.map((error) => ({
      code: error.code,
      detail: error.detail,
      ...(error.equationId === undefined ? {} : { equationId: error.equationId, label: labels.get(error.equationId) }),
      ...(error.token === undefined ? {} : { token: error.token }),
      ...(error.position === undefined ? {} : { position: error.position }),
    }));

    return { compiled, built, run, errors };
  }, [equations, sample, templateValues]);

  const compileErrorsByEquation = useMemo(() => errorsByEquation(preview.errors), [preview.errors]);
  const signature = useMemo(() => equationsSignature(equations), [equations]);
  const validationMatches = validation !== null && validation.signature === signature;
  const canPublish = preview.compiled.ok && validationMatches && validation!.ok && draftId !== null && reason.trim() !== '';
  // Like the validation above, the impact preview describes the equations it was run on and nothing
  // else: an edit since then makes its count a statement about a policy that no longer exists.
  const impactForTheseEquations = impact !== null && impact.signature === signature ? impact.result : null;

  const describeError = useCallback(
    (error: { code: string; detail: string; token?: string }): string =>
      t('equations.errorLine', { code: t(`equations.errorCodes.${error.code}`), detail: error.detail }),
    [t],
  );

  const resultFor = (id: string, unit: UnitName): { result: string | null; error: string | null } => {
    const compileError = compileErrorsByEquation.get(id)?.[0];

    if (compileError) {
      return { result: null, error: describeError(compileError) };
    }

    const trace = preview.run.trace[id];

    if (trace?.error) {
      return { result: null, error: describeError(trace.error) };
    }

    if (trace?.skipped) {
      return { result: t('equations.skipped'), error: null };
    }

    return {
      result: trace && trace.value !== null ? formatEquationResult(trace.value, unit, t(`equations.units.${unit}`)) : null,
      error: null,
    };
  };

  const onValidate = async (): Promise<void> => {
    setActionError(null);

    try {
      const result = await validateDraft.mutateAsync({ equations });

      setValidation({ signature: equationsSignature(equations), ok: result.ok, errors: result.errors });
    } catch (error: unknown) {
      setActionError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  const onPublish = async (): Promise<void> => {
    if (draftId === null || revision === null) {
      return;
    }

    setActionError(null);

    try {
      await publishPolicy.mutateAsync({ versionId: draftId, expectedRevision: revision, reason: reason.trim(), scope: publishScope });

      setIsPublishOpen(false);
      setValidation(null);
      setReloadToken((token) => token + 1);
      await Promise.all([
        invalidateVersions(),
        queryClient.invalidateQueries({ queryKey: orpc.policies.getPublished.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.policies.getVersion.key() }),
        // `OPEN_ESTIMATES` re-prices records this page does not show, so everything estimate-shaped is
        // refetched rather than left holding the old policy's figures.
        queryClient.invalidateQueries({ queryKey: orpc.estimates.get.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.estimates.list.key() }),
      ]);
    } catch (error: unknown) {
      setActionError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  const onOpenAsDraft = useCallback(
    async (versionId: string): Promise<void> => {
      setActionError(null);

      try {
        const draft = await createDraft.mutateAsync({ fromVersionId: versionId });

        setDraftId(draft.id);
        setRevision(draft.revision);
        setReloadToken((token) => token + 1);
        setLoadedKey(null);
        await invalidateVersions();
      } catch (error: unknown) {
        setActionError(serverMessage(error) ?? t(submitErrorKey(error)));
      }
    },
    [createDraft, invalidateVersions, t],
  );

  const grouped = useMemo(() => groupEquations(equations), [equations]);
  const wideErrors = useMemo(() => policyWideErrors(preview.errors), [preview.errors]);
  const inputs: readonly PolicyInput[] = catalogQuery.data ?? (INPUT_CATALOG as readonly PolicyInput[]);

  if (publishedQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (publishedQuery.isError) {
    return <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>;
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('equations.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('equations.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {publishedQuery.data ? <Badge mono>{t('equations.publishedBadge', { version: publishedQuery.data.version })}</Badge> : null}
          {draftId !== null && draftQuery.data ? <Badge tone="info">{t('equations.draftBadge', { version: draftQuery.data.version })}</Badge> : null}
          {isSaving ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Spinner className="size-3" />
              {t('equations.draftSaving')}
            </span>
          ) : savedAt !== null ? (
            <span className="text-xs text-muted-foreground">{t('equations.draftSaved')}</span>
          ) : null}
        </div>
      </header>

      <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">{t('equations.notice')}</p>

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      {preview.errors.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          <p className="font-medium">{t('equations.errorsTitle', { count: preview.errors.length })}</p>
          <ul className="mt-2 space-y-1">
            {preview.errors.slice(0, 12).map((error, index) => (
              <li key={`${error.code}-${error.equationId ?? 'policy'}-${index}`}>
                {error.label ? `${error.label}: ` : error.equationId ? `${error.equationId}: ` : ''}
                {describeError(error)}
              </li>
            ))}
          </ul>
        </div>
      ) : wideErrors.length === 0 && validationMatches && validation!.ok ? (
        <p className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          <CheckCircle2 className="size-4 text-[#15803d]" />
          {t('equations.validated')}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          {EQUATION_GROUP_ORDER.map((group) => (
            <section
              key={group}
              className="rounded-lg border border-border bg-card p-5"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t(`equations.groups.${group}`)}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addCustomEquation(group)}
                >
                  <Plus className="size-3.5" />
                  {t('equations.addCustom')}
                </Button>
              </div>

              <div className="space-y-5">
                {(grouped.get(group) ?? []).map((equation) => {
                  const { result, error } = resultFor(equation.id, equation.unit);

                  return (
                    <EquationRow
                      key={equation.id}
                      equation={equation}
                      result={result}
                      error={error}
                      removeBlockedBy={equation.kind === 'CUSTOM' ? referencesTo(equations, equation.id) : undefined}
                      onChange={(patch) => changeEquation(equation.id, patch)}
                      onRemove={() => removeEquation(equation.id)}
                    />
                  );
                })}
              </div>
            </section>
          ))}

          <section className="space-y-4 rounded-lg border border-border bg-card p-5">
            <FormField
              id="equations-reason"
              label={t('equations.reason')}
              hint={t('equations.reasonHint')}
            >
              <Textarea
                id="equations-reason"
                rows={3}
                value={reason}
                placeholder={t('equations.reasonPlaceholder')}
                onChange={(event) => changeReason(event.target.value)}
              />
            </FormField>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                isLoading={validateDraft.isPending}
                onClick={() => void onValidate()}
              >
                {t('equations.validate')}
              </Button>
              <Button
                disabled={!canPublish}
                onClick={() => setIsPublishOpen(true)}
              >
                <Upload className="size-4" />
                {t('equations.publish')}
              </Button>
              {!canPublish ? (
                <span className="text-xs text-muted-foreground">
                  {preview.errors.length > 0
                    ? t('equations.publishBlockedHint')
                    : draftId === null
                      ? t('equations.publishNeedsDraft')
                      : reason.trim() === ''
                        ? t('equations.publishNeedsReason')
                        : t('equations.publishNeedsValidation')}
                </span>
              ) : null}
            </div>
          </section>

          <ImpactPreviewCard
            equations={equations}
            disabled={!preview.compiled.ok}
            onResult={(result) => setImpact({ signature, result })}
          />

          <section className="space-y-4 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('equations.versions')}</h2>
            <PolicyVersionsTable
              versions={versionsQuery.data ?? []}
              isLoading={versionsQuery.isLoading}
              isOpening={createDraft.isPending}
              onOpenAsDraft={(versionId) => void onOpenAsDraft(versionId)}
            />
          </section>
        </div>

        <SamplePreviewCard
          sample={sample}
          onSampleChange={setSample}
          run={preview.run}
          built={preview.built}
          inputs={inputs}
          valuesOptions={valuesOptions}
          valuesFrom={valuesFrom}
          onValuesFromChange={setValuesFrom}
          activeValues={templateValues ? selectedValues : null}
        />
      </div>

      <Dialog
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        title={t('equations.publishTitle')}
        description={t('equations.publishBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsPublishOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={publishPolicy.isPending}
              disabled={!canPublish}
              onClick={() => void onPublish()}
            >
              {t('equations.publishConfirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField
            id="publish-reason"
            label={t('equations.reason')}
          >
            <Textarea
              id="publish-reason"
              rows={3}
              value={reason}
              onChange={(event) => changeReason(event.target.value)}
            />
          </FormField>

          <FormField
            id="publish-scope"
            label={t('equations.publishScope')}
            hint={publishScope === 'NEW' ? t('equations.scopeNewHint') : t('equations.scopeOpenEstimatesHint')}
          >
            <Select
              id="publish-scope"
              value={publishScope}
              onChange={(event) => setPublishScope(event.target.value as PublishPolicyScope)}
              options={[
                { value: 'NEW', label: t('equations.scopeNew') },
                { value: 'OPEN_ESTIMATES', label: t('equations.scopeOpenEstimates') },
              ]}
            />
          </FormField>

          {/* Re-pricing open estimates moves numbers on records nobody has touched, so the scope that
              does it says how many of them these equations could not price at all (plan §11 fix 15). */}
          {publishScope === 'OPEN_ESTIMATES' ? (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {impactForTheseEquations === null
                ? t('equations.impactHint')
                : t('equations.publishIncompleteNotice', { count: impactForTheseEquations.incompleteCount })}
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
};
